import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabase';
import { getUserData } from '../utils';

/**
 * Hook per ottenere il prossimo numero di preventivo disponibile
 * IMPORTANTE: Questo hook NON incrementa il contatore, mostra solo il prossimo numero
 * che sarebbe disponibile. Il contatore verrà incrementato solo quando il deal sarà salvato.
 */
export function useNextQuoteNumber() {
  const [nextQuoteNumber, setNextQuoteNumber] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [debugInfo, setDebugInfo] = useState<any>(null);
  
  // Generate a persistent hook instance ID for this component instance
  const hookInstanceId = useState(() => `hook_${Math.random().toString(36).substring(2, 10)}`)[0];

  // Extract the fetch function so we can call it manually for refreshes
  const fetchNextQuoteNumber = useCallback(async (forceRefresh = false) => {
    try {
      setLoading(true);
      setError(null);
      
      // Ottieni i dati dell'utente corrente
      const userData = await getUserData();
      
      if (!userData?.organizationId) {
        console.error(`❌ [${hookInstanceId}] Organizzazione non trovata nei dati utente`);
        setError('Nessuna organizzazione associata all\'utente');
        
        // Provide a default formatted quote number even when we can't get organization ID
        const currentYear = new Date().getFullYear();
        const defaultNumber = `001/${currentYear}`;
        setNextQuoteNumber(defaultNumber);
        
        setLoading(false);
        return;
      }
      
      const organizationId = userData.organizationId;
      const currentYear = new Date().getFullYear();
      
      console.log(`🔄 [${hookInstanceId}] Ottenimento numero preventivo per organizzazione ${organizationId}`);
      
      // Per evitare qualsiasi problema con la cache di Supabase,
      // facciamo un'unica query con RPC (funzione SQL) che bypassa completamente 
      // tutti i livelli di cache, evitando anche la cache della funzione postgres
      const timestamp = Date.now();
      
      console.log(`🔄 [${hookInstanceId}] Esecuzione query diretta su settings, timestamp: ${timestamp}`);
      
      // Usiamo una query RPC già esistente oppure una query standard
      // Testiamo se esiste la funzione get_quote_number_info
      let settingsData;
      let settingsError;
      
      try {
        // Proviamo la query standard prima
        const response = await supabase
          .from('settings')
          .select(`id, last_quote_number, last_quote_year, quote_number_start, updated_at`)
          .eq('organization_id', organizationId)
          .order('updated_at', { ascending: false })
          .limit(1);
          
        settingsData = response.data;
        settingsError = response.error;
      } catch (e) {
        console.error(`❌ [${hookInstanceId}] Errore nella query settings:`, e);
        
        // Always ensure we have a formatted quote number to return
        const defaultNumber = `001/${currentYear}`;
        setNextQuoteNumber(defaultNumber);
        setDebugInfo({
          organizationId,
          currentYear,
          error: e,
          defaultNumber
        });
        
        setError('Errore nella query');
        setLoading(false);
        return;
      }
      
      // Controlla prima se abbiamo ricevuto un array vuoto
      if (!settingsData || settingsData.length === 0 || settingsError) {
        console.warn(`⚠️ [${hookInstanceId}] Nessuna impostazione trovata:`, settingsError || 'Array vuoto');
        
        // Se non ci sono impostazioni, dobbiamo comunque fornire un numero di preventivo
        // Usiamo il valore di default 001/ANNO
        const defaultNumber = `001/${currentYear}`;
        setNextQuoteNumber(defaultNumber);
        
        setDebugInfo({
          organizationId,
          currentYear,
          error: settingsError,
          defaultNumber,
          reason: "No settings found"
        });
        
        setError('Impostazioni non trovate');
        setLoading(false);
        return;
      }
      
      // Prendi il primo risultato (il più recente)
      const settingsRow = settingsData[0];
      
      // Salva i dati grezzi per debug
      setDebugInfo({
        organizationId,
        currentYear,
        settings: settingsRow,
        timestamp
      });
      
      console.log(`📋 [${hookInstanceId}] Impostazioni recuperate:`, { 
        last_quote_number: settingsRow.last_quote_number,
        last_quote_year: settingsRow.last_quote_year,
        quote_number_start: settingsRow.quote_number_start,
        updated_at: settingsRow.updated_at
      });
      
      // Calcola il prossimo numero (senza incrementare sul database)
      let nextNumber: number;
      
      if (settingsRow.last_quote_year !== currentYear) {
        // Se l'anno è cambiato, usa il numero iniziale configurato
        nextNumber = settingsRow.quote_number_start || 1;
        console.log(`🔄 [${hookInstanceId}] Anno diverso (${settingsRow.last_quote_year} -> ${currentYear}), prossimo numero: ${nextNumber}`);
      } else {
        // Altrimenti, prevedi l'incremento di 1
        nextNumber = (settingsRow.last_quote_number || 0) + 1;
        console.log(`🔄 [${hookInstanceId}] Stesso anno (${currentYear}), incremento: ${settingsRow.last_quote_number} -> ${nextNumber}`);
      }
      
      // Formatta il numero con padding
      const formattedNumber = nextNumber.toString().padStart(3, '0');
      const quoteNumber = `${formattedNumber}/${currentYear}`;
      
      console.log(`✅ [${hookInstanceId}] Calcolato prossimo numero preventivo: ${quoteNumber} (basato su ${settingsRow.last_quote_number})`);
      
      // Aggiorna lo stato
      setNextQuoteNumber(quoteNumber);
      setLoading(false);
    } catch (err) {
      console.error(`❌ [${hookInstanceId}] Errore nella previsione del numero preventivo:`, err);
      
      // Always ensure we have a formatted quote number even on errors
      const currentYear = new Date().getFullYear();
      const defaultNumber = `001/${currentYear}`;
      setNextQuoteNumber(defaultNumber);
      
      setDebugInfo({
        error: err,
        defaultNumber
      });
      
      setError('Errore imprevisto');
      setLoading(false);
    }
  }, [hookInstanceId]);

  // Effetto iniziale - carica il numero al mount del componente
  useEffect(() => {
    console.log(`🔍 [${hookInstanceId}] Hook useNextQuoteNumber inizializzato`);
    
    fetchNextQuoteNumber(false);
    
    // Cleanup function
    return () => {
      console.log(`🧹 [${hookInstanceId}] Hook useNextQuoteNumber cleanup`);
    };
  }, [fetchNextQuoteNumber, hookInstanceId]);
  
  // Aggiungiamo debugInfo all'output del hook
  return { 
    nextQuoteNumber,
    loading, 
    error, 
    debugInfo,
    refreshQuoteNumber: () => fetchNextQuoteNumber(true) // Funzione per forzare un refresh
  };
}